<template>
  <div class="login-container">
    <a-card class="login-card" :bordered="false">
      <!-- 卡片头部 -->
      <div class="card-header">
        <div class="brand-logo">
          <rocket-two-tone :style="{ fontSize: '40px' }" />
        </div>
        <h1 class="system-name">柳南临工招聘管理系统</h1>
        <p class="welcome-text">欢迎回来，请登录您的账号</p>
      </div>

      <!-- 登录表单 -->
      <a-form
        layout="vertical"
        :model="formState"
        :rules="rules"
        @finish="handleLogin"
        class="login-form"
      >
        <a-form-item label="账号" name="username">
          <a-input
            v-model:value="formState.username"
            placeholder="请输入账号"
            size="large"
            allow-clear
          >
            <template #prefix>
              <user-outlined class="input-icon" />
            </template>
          </a-input>
        </a-form-item>

        <a-form-item label="密码" name="password">
          <a-input-password
            v-model:value="formState.password"
            placeholder="请输入密码"
            size="large"
            allow-clear
          >
            <template #prefix>
              <lock-outlined class="input-icon" />
            </template>
          </a-input-password>
        </a-form-item>

        <a-form-item label="验证码" name="captcha">
          <div style="display: flex; gap: 10px">
            <a-input
              v-model:value="formState.captcha"
              placeholder="请输入验证码"
              size="large"
              allow-clear
            >
              <template #prefix>
                <lock-outlined class="input-icon" />
              </template>
            </a-input>
            <a-image
              :preview="false"
              height="40px"
              width="120px"
              :src="captchaImg"
              @click="loadCaptcha"
            />
          </div>
        </a-form-item>

        <a-form-item>
          <a-button
            type="primary"
            html-type="submit"
            size="large"
            :loading="loading"
            block
            class="login-btn"
          >
            {{ loading ? "正在登录..." : "立即登录" }}
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>
  </div>
</template>

<script setup>
import { nextTick, onMounted, reactive, ref, unref } from "vue";
import {
  UserOutlined,
  LockOutlined,
  RocketTwoTone,
} from "@ant-design/icons-vue";
import { router } from "@/route/index.js";
import useUserStore from "@/store/userStore.js";
import { notification } from "ant-design-vue";
import { getRequest } from "@/utils/request.js";

const userStore = useUserStore();

const formState = reactive({
  username: "",
  password: "",
  captcha: "",
});

const loading = ref(false);
const rules = {
  username: [
    { required: true, message: "请输入账号", trigger: "blur" },
    { min: 4, max: 16, message: "账号长度为4-16位", trigger: "blur" },
  ],
  password: [
    { required: true, message: "请输入密码", trigger: "blur" },
    { min: 6, max: 18, message: "密码长度为6-18位", trigger: "blur" },
  ],
  captcha: [{ required: true, message: "请输入验证码", trigger: "blur" }],
};

const handleLogin = () => {
  loading.value = true;
  const { username, password, captcha } = formState;
  userStore
    .login(username, password, captchaKey.value, captcha)
    .then(() => {
      nextTick().then(() => {
        router.push("/index");
      });
    })
    .catch((e) => {
      notification.error({
        message: "登录失败",
        description: e,
      });
    })
    .finally(() => {
      loading.value = false;
    });
};

const captchaImg = ref(null);
const captchaKey = ref(null);

const loadCaptcha = () => {
  getRequest("/captcha").then((response) => {
    console.info("response", response);
    if (response.success) {
      captchaImg.value = response.data.captchaImg;
      captchaKey.value = response.data.captchaKey;
      formState.captcha = "";
    }
  });
};

onMounted(() => {
  loadCaptcha();
});
</script>

<style lang="scss" scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fb 0%, #e9ecef 100%);
  padding: 24px;
  animation: bg-pan 15s infinite alternate;

  @keyframes bg-pan {
    0% {
      background-position: 0% 50%;
    }
    100% {
      background-position: 100% 50%;
    }
  }
}

.login-card {
  width: 100%;
  max-width: 440px;
  border-radius: 16px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.05);
  transform: translateY(20px);
  opacity: 0;
  animation: card-enter 0.6s cubic-bezier(0.23, 1, 0.32, 1) forwards;

  :deep(.ant-card-body) {
    padding: 40px;
  }

  @keyframes card-enter {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

.card-header {
  text-align: center;
  margin-bottom: 32px;

  .brand-logo {
    display: inline-flex;
    padding: 16px;
    background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
    border-radius: 50%;
    margin-bottom: 24px;
    transition: transform 0.3s ease;

    &:hover {
      transform: rotate(15deg);
    }
  }

  .system-name {
    color: #1e293b;
    font-size: 24px;
    margin-bottom: 8px;
  }

  .welcome-text {
    color: #64748b;
    font-size: 14px;
  }
}

.login-form {
  .ant-form-item-label > label {
    color: #475569;
    font-weight: 500;
  }

  :deep(.ant-input-affix-wrapper) {
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;

    &:hover,
    &.ant-input-affix-wrapper-focused {
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }
  }

  .input-icon {
    color: #94a3b8;
  }
}

.login-btn {
  height: 48px;
  border-radius: 8px;
  font-weight: 500;
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  border: none;
  transition: all 0.3s ease;

  &:not([disabled]):hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
  }

  &:active {
    transform: translateY(1px);
  }
}

@media (max-width: 480px) {
  .login-card {
    :deep(.ant-card-body) {
      padding: 24px;
    }
  }
}
</style>
